import React from "react";
import { clsx } from "clsx";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { Button } from "./Button";

const pageItems = (page, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const items = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  if (start > 2) items.push("start-ellipsis");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < totalPages - 1) items.push("end-ellipsis");
  items.push(totalPages);
  return items;
};

/**
 * Pagination Component
 *
 * Result-count summary plus windowed numbered page controls.
 *
 * @example
 * <Pagination page={2} limit={25} total={140} onPageChange={setPage} />
 */
export const Pagination = ({ page, limit, total, onPageChange, className }) => {
  const totalPages = Math.max(Math.ceil(total / limit), 0);
  const first = total > 0 ? (page - 1) * limit + 1 : 0;
  const last = Math.min(page * limit, total);

  return (
    <div
      className={clsx(
        "p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4",
        className,
      )}
    >
      <p className="text-sm text-gray-500">
        Showing <span className="font-medium text-gray-700">{first}</span> to{" "}
        <span className="font-medium text-gray-700">{last}</span> of{" "}
        <span className="font-medium text-gray-700">{total}</span> results
      </p>
      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            leftIcon={<ChevronLeftIcon className="size-4" />}
          >
            Previous
          </Button>
          {pageItems(page, totalPages).map((item) =>
            typeof item === "string" ? (
              <span
                key={item}
                aria-hidden="true"
                className="px-1.5 text-sm text-gray-400 select-none"
              >
                &hellip;
              </span>
            ) : (
              <button
                key={item}
                type="button"
                onClick={() => onPageChange(item)}
                aria-current={item === page ? "page" : undefined}
                aria-label={`Page ${item}`}
                className={clsx(
                  "min-w-[2.25rem] h-9 px-2 rounded-lg text-sm font-medium transition-colors",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500/50",
                  item === page
                    ? "bg-wrg-blue-500 text-white shadow-sm"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                )}
              >
                {item}
              </button>
            ),
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            rightIcon={<ChevronRightIcon className="size-4" />}
          >
            Next
          </Button>
        </nav>
      )}
    </div>
  );
};

export default Pagination;
